import throttle from '../utils/throttle'
import animateProperty from '../utils/animate-property'
import { getElementHorizontalMetrics } from '../utils/get-element-metrics'

/**
 * Find the closest value below or equal to given numeric value in an array of numbers that are
 * sorted from low to high
 * @param arr {Array}
 * @param value {Number}
 */
function findIndexOfClosestValueBelowOrEqualToGivenValue(arr, value) {
	let index = arr.length - 1
	for (index; index >= 0; index--) {
		if (value >= arr[index]) break
	}
	return index
}

/**
 * Returns a value between 0 and max
 * @param target {Number}
 * @param max {Number}
 */
function ensureBounds(target, max) {
	return Math.min(Math.max(target, 0), max)
}

class Carousel {
	constructor (el, options) {
		options = options || {}
		const { slideTime, initialSlide, scrollThrottleTime, scrollbarVisible} = options

		this.el = el
		this.slideTime = slideTime || 850
		this.initialSlide = initialSlide || 0
		this.scrollThrottleTime = scrollThrottleTime || 150
		this.scrollbarVisible = scrollbarVisible || false

		this._handleNextClick = this._handleNextClick.bind(this)
		this._handlePrevClick = this._handlePrevClick.bind(this)
		this._handleScroll = this._handleScroll.bind(this)
		this._handleWindowResize = this._handleWindowResize.bind(this)
		this._intersectionObserverCallback = this._intersectionObserverCallback.bind(this)

		this._scrollContainer = this.el.querySelector('.carousel__items')
		this._startSentinel = this._scrollContainer.querySelector('.carousel__sentinel--start')
		this._endSentinel = this._scrollContainer.querySelector('.carousel__sentinel--end')
		this._slides = Array.from(this._scrollContainer.querySelector('.carousel__items-inner').children).filter(el => !el.classList.contains('carousel__sentinel'))

		this._next = this.el.querySelector('.carousel__btn--next')
		this._prev = this.el.querySelector('.carousel__btn--prev')

		this._numSlides = this._slides.length
		this._width = this._scrollContainer.offsetWidth
		this._scrollWidth = this._scrollContainer.scrollWidth
		this._sentinelWidth = this._startSentinel.offsetWidth
		this._animationsRunning = 0
		this._nextVisible = false
		this._prevVisible = false

		this._scrollLeftThresholdsToChangeActiveAt = this._computeScrollLeftThresholdsToChangeActiveAt(this._slides)

		this._activeIndex = 0
		this._scrollLeft = 0

		this._updateClasses()

		this._next.addEventListener('click', this._handleNextClick)
		this._prev.addEventListener('click', this._handlePrevClick)
		this._scrollContainer.addEventListener('scroll', this._handleScroll)
		window.addEventListener('resize', this._handleWindowResize)

		this._setupIntersectionObserver()

		if (this.initialSlide !== 0) {
			this._doShowSlide(ensureBounds(this.initialSlide, this._slides.length - 1))
		}
	}

	/**
	 * @private
	 */
	_updateClasses () {
		this._scrollContainer.classList[this.scrollbarVisible ? 'add' : 'remove']('carousel--scrollbar-visible')
	}

	/**
	 * @private
	 */
	_handleWindowResize() {
		// Bail if we only resized vertically because iOS safari fires resize when chrome shows/hides
		if (this._scrollContainer.offsetWidth === this._width) {
			return
		}
		this._width = this._scrollContainer.offsetWidth
		this._scrollWidth = this._scrollContainer.scrollWidth
		this._sentinelWidth = this._startSentinel.offsetWidth
		this._scrollLeftThresholdsToChangeActiveAt = this._computeScrollLeftThresholdsToChangeActiveAt(this._slides)
	}

	/**
	 * @private
	 */
	_setupIntersectionObserver() {
		if (this._io) {
			this._io.disconnect()
		}
		this._io = new IntersectionObserver(this._intersectionObserverCallback, {
			root: this._scrollContainer,
			threshold: [0, 0.5, 1]
		})
		this._slides.forEach(item => this._io.observe(item))
		this._io.observe(this._startSentinel)
		this._io.observe(this._endSentinel)
	}

	/**
	 * @private
	 */
	_intersectionObserverCallback(entries) {
		if (this._animationsRunning > 0) {
			return
		}

		let startSentinelVisibility
		let endSentinelVisibility

		entries.forEach(entry => {
			if (!startSentinelVisibility && entry.target === this._startSentinel) {
				startSentinelVisibility = entry.intersectionRatio < 0.5
			} else if (!endSentinelVisibility && entry.target === this._endSentinel) {
				endSentinelVisibility = entry.intersectionRatio < 0.5
			}
		})

		const newActive = findIndexOfClosestValueBelowOrEqualToGivenValue(
			this._scrollLeftThresholdsToChangeActiveAt,
			this._scrollLeft
		)
		if (newActive !== this._activeIndex) {
			this._setActiveIndex(ensureBounds(newActive, this._slides.length - 1))
		}

		requestAnimationFrame(() => {
			this._setVisibilityOfPrevAndNextButtons(startSentinelVisibility, endSentinelVisibility)
		})
	}

	/**
	 * @private
	 */
	_handleScroll(event) {
		// Cannot access event.target in a throttled scroll handler, because the event might have
		// bubbled in the meantime so read it off this._scrollContainer
		this._scrollLeft = ((event && event.target) || this._scrollContainer).scrollLeft
	}

	/**
	 * @private
	 */
	_isScrollLeftInVicinityOfStartOrEnd(scrollLeft, scrollWidth = this._scrollWidth, width = this._width, vicinity = this._sentinelWidth) {
		return {
			isInVicinityOfStart: scrollLeft < vicinity,
			isInVicinityOfEnd: scrollLeft > scrollWidth - width - vicinity
		}
	}

	/**
	 * Sets visibility of prev and next. Attempts to do as little possible by checking flags
	 * @private
	 * @param [showPrev] {Boolean} Value to set visibility to (if not already set and not undefined)
	 * @param [showPrev] {Boolean} Value to set visibility to (if not already set and not undefined)
	 */
	_setVisibilityOfPrevAndNextButtons(showPrev, showNext) {
		if ((this._prevVisible && showPrev === false) || (!this._prevVisible && showPrev === true)) {
			this._prevVisible = !this._prevVisible
			this._toggleButtonDisabledAttributes(this._prev, this._prevVisible)
		}
		if ((this._nextVisible && showNext === false) || (!this._nextVisible && showNext === true)) {
			this._nextVisible = !this._nextVisible
			this._toggleButtonDisabledAttributes(this._next, this._nextVisible)
		}
	}

	/**
	 * Sets a new active index and broadcast change and before-change
	 * @private
	 * @param button {HTMLElement} Element to set attributes on.
	 * @param enable {Boolean} Whether or not to remove the disabled characteristics
	 */
	_toggleButtonDisabledAttributes(button, enable) {
		if (enable) {
			button.classList.remove('carousel__btn--disabled')
			button.children[0] && button.children[0].removeAttribute('disabled')
		} else {
			button.classList.add('carousel__btn--disabled')
			button.children[0] && button.children[0].setAttribute('disabled', 'disabled')
		}
	}

	/**
	 * Sets a new active index and broadcast change and before-change
	 * @private
	 * @param index {Number} Index to set.
	 */
	_setActiveIndex(index) {
		if (this._activeIndex === index) {
			return
		}
		this.el.dispatchEvent(new CustomEvent('before-change', { detail: { index: this._activeIndex } }))
		this._activeIndex = index
		this.el.dispatchEvent(new CustomEvent('change', { detail: { index }}))
	}

	/**
	 * Animate to a new slide index if not already active
	 * @private
	 * @param index {Number} The index of the slide to get target scrollLeft for
	 */
	_getTargetScrollLeftForSlide(index) {
		const { offsetLeft, offsetWidth } = getElementHorizontalMetrics(this._slides[index])
		const targetScrollLeft = Math.max(offsetLeft - (this._width / 2) + offsetWidth / 2, 0)

		return Math.min(this._scrollWidth - this._width, targetScrollLeft)
	}

	/**
	 * Animate to a new slide index if not already active
	 * @public
	 * @param index {Number} The index of the slide to show
	 * @param [instant] {Boolean}
	 */
	showSlide(index, instant) {
		if (this._activeIndex === index) {
			return
		}

		index = ensureBounds(index, this._slides.length - 1)
		this._doShowSlide(index, instant)
	}

	/**
	 * Do the actual animation and set the new index
	 * @private
	 * @param index {Number} The index of the slide to show
	 * @param [instant] {Boolean}
	 */
	_doShowSlide(index, instant) {
		const targetScrollLeft = this._getTargetScrollLeftForSlide(index)
		const { isInVicinityOfStart, isInVicinityOfEnd } = this._isScrollLeftInVicinityOfStartOrEnd(targetScrollLeft)

		this._setActiveIndex(index)
		this._animationsRunning += 1

		requestAnimationFrame(() => {
			this._setVisibilityOfPrevAndNextButtons(!isInVicinityOfStart, !isInVicinityOfEnd)

			animateProperty(this._scrollContainer, 'scrollLeft', targetScrollLeft, () => {
				// Ensure the throttle timeout has run before decrementing
				setTimeout(() => (this._animationsRunning -= 1), this.scrollThrottleTime)
			}, instant ? 0 : this.slideTime)
		})
	}

	/**
	 * Handle next button click. This wraps the slot so implementers do not have to
	 * worry about implementing show/hide functionality themselves
	 * @private
	 */
	_handleNextClick() {
		if (this._nextVisible) {
			this._doShowSlide(ensureBounds(this._activeIndex + 1, this._slides.length - 1))
		}
	}

	/**
	 * Handle prev button click. This wraps the slot so implementers do not have to
	 * worry about implementing show/hide functionality themselves
	 * @private
	 */
	_handlePrevClick() {
		if (this._prevVisible) {
			this._doShowSlide(ensureBounds(this._activeIndex - 1, this._slides.length - 1))
		}
	}

	/**
	* Returns an array off offsets to snap to for each given slide
	* Beware: Causes reflow, so should only be called when absolutely neccessary
	* @private
	*/
	_computeScrollLeftThresholdsToChangeActiveAt(slides, width = this._width) {
		let lastOffsetWidth = 0
		let lastOffsetLeft = 0

		return slides.map((node, index) => {
			const { offsetLeft, offsetWidth } = getElementHorizontalMetrics(node)
			const halfScreenWidth = width / 2
			const gutterOnLeftSideOfItem = offsetLeft - (lastOffsetLeft + lastOffsetWidth)

			lastOffsetLeft = offsetLeft
			lastOffsetWidth = offsetWidth

			return offsetLeft - halfScreenWidth - gutterOnLeftSideOfItem / 2
		})
	}
}

export default Carousel
